/**
 * Convert stored HTML fragment to TipTap-compatible HTML.
 * Input is a fragment like: "Some text with <b>bold</b> and <i>italic</i>"
 * Output wraps it in a paragraph for TipTap: "<p>Some text with <b>bold</b> and <i>italic</i></p>"
 */
export function fragmentToHtml(fragment: string): string {
  if (!fragment) return '<p></p>';
  return `<p>${fragment}</p>`;
}

/**
 * Convert TipTap's HTML output back to a stored fragment.
 * Input: "<p>Some text with <strong>bold</strong> and <em>italic</em></p>"
 * Output: "Some text with <b>bold</b> and <i>italic</i>"
 *
 * Rules:
 * - Strip the outer <p>...</p>
 * - Map <strong> to <b> and <em> to <i>
 * - Keep <a href="..."> but remove all other attributes (target, rel, class, etc.)
 * - Remove any other tags
 */
export function htmlToFragment(html: string): string {
  if (!html) return '';

  // Remove outer <p> tags
  let cleaned = html.replace(/^<p>/i, '').replace(/<\/p>$/i, '');

  // Create a temporary div to leverage DOM parsing for safety
  const temp = document.createElement('div');
  temp.innerHTML = cleaned;

  // Convert <strong> to <b> and <em> to <i>
  temp.querySelectorAll('strong').forEach((el) => {
    const b = document.createElement('b');
    b.innerHTML = el.innerHTML;
    el.replaceWith(b);
  });

  temp.querySelectorAll('em').forEach((el) => {
    const i = document.createElement('i');
    i.innerHTML = el.innerHTML;
    el.replaceWith(i);
  });

  // For <a> tags, keep only href attribute
  temp.querySelectorAll('a').forEach((el) => {
    const href = el.getAttribute('href');
    const newA = document.createElement('a');
    if (href) {
      newA.setAttribute('href', href);
    }
    newA.innerHTML = el.innerHTML;
    el.replaceWith(newA);
  });

  // Remove any remaining disallowed tags by extracting text and allowed children
  const result = sanitizeToAllowedTags(temp);

  return result.innerHTML;
}

/**
 * Recursively process a node to keep only allowed tags: b, i, a (with href only)
 */
function sanitizeToAllowedTags(node: Element | Node): Element {
  const result = document.createElement('div');

  const processNode = (n: Node) => {
    if (n.nodeType === Node.TEXT_NODE) {
      result.appendChild(n.cloneNode());
    } else if (n.nodeType === Node.ELEMENT_NODE) {
      const el = n as Element;
      const tagName = el.tagName.toLowerCase();

      if (tagName === 'b' || tagName === 'strong') {
        const b = document.createElement('b');
        b.innerHTML = sanitizeToAllowedTags(el).innerHTML;
        result.appendChild(b);
      } else if (tagName === 'i' || tagName === 'em') {
        const i = document.createElement('i');
        i.innerHTML = sanitizeToAllowedTags(el).innerHTML;
        result.appendChild(i);
      } else if (tagName === 'a') {
        const href = el.getAttribute('href');
        const a = document.createElement('a');
        if (href) a.setAttribute('href', href);
        a.innerHTML = sanitizeToAllowedTags(el).innerHTML;
        result.appendChild(a);
      } else {
        // Disallowed tag: flatten children into current context
        el.childNodes.forEach(processNode);
      }
    }
  };

  node.childNodes.forEach(processNode);
  return result;
}
